import React, { ReactNode, useEffect, useId, useRef } from "react";
import { FiX } from "react-icons/fi";

interface CommentDialogProps {
    title: string;
    description?: string;
    children: ReactNode;
    onClose: () => void;
    onFocusChange?: (focused: boolean) => void;
}

const CommentDialog: React.FC<CommentDialogProps> = ({
    title,
    description,
    children,
    onClose,
    onFocusChange,
}) => {
    const titleId = useId();
    const descriptionId = useId();
    const panelRef = useRef<HTMLDivElement>(null);
    const onCloseRef = useRef(onClose);
    const onFocusChangeRef = useRef(onFocusChange);
    onCloseRef.current = onClose;
    onFocusChangeRef.current = onFocusChange;

    useEffect(() => {
        const previouslyFocused = document.activeElement as HTMLElement | null;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        onFocusChangeRef.current?.(true);
        const firstFocusable = panelRef.current?.querySelector<HTMLElement>(
            "button, select, textarea, input, [tabindex]:not([tabindex='-1'])"
        );
        firstFocusable?.focus();

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") onCloseRef.current();
            if (event.key !== "Tab") return;

            const focusable = Array.from(
                panelRef.current?.querySelectorAll<HTMLElement>(
                    "button:not(:disabled), select:not(:disabled), textarea:not(:disabled), input:not(:disabled), [tabindex]:not([tabindex='-1'])"
                ) ?? []
            );
            if (focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("keydown", onKeyDown);
            document.body.style.overflow = previousOverflow;
            onFocusChangeRef.current?.(false);
            previouslyFocused?.focus();
        };
    }, []);

    return (
        <div
            className="video-comments__dialog-backdrop"
            onMouseDown={(event) => {
                if (event.currentTarget === event.target) onClose();
            }}
        >
            <div
                ref={panelRef}
                className="video-comments__dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={description ? descriptionId : undefined}
            >
                <div className="video-comments__dialog-header">
                    <h3 id={titleId}>{title}</h3>
                    <button
                        type="button"
                        className="video-comments__icon-button"
                        onClick={onClose}
                        aria-label="Close dialog"
                    >
                        <FiX aria-hidden="true" />
                    </button>
                </div>
                {description && <p id={descriptionId}>{description}</p>}
                {children}
            </div>
        </div>
    );
};

export default CommentDialog;
